import { Divider, Modal, tinyButtons, Toast } from '@byecode/ui'
import type { DragEndEvent, DragMoveEvent, DragStartEvent, Modifier } from '@dnd-kit/core'
import { closestCenter, DndContext, DragOverlay, MeasuringStrategy, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { getBlockIcon, getBlockName } from '@lighthouse/block'
import type { BlockAbstract, ContainerBlockAbstract, DataSourceAbstract, ViewBlockAbstract } from '@lighthouse/core'
import { BlockType } from '@lighthouse/core'
import type { CustomViewVisibleData } from '@lighthouse/shared'
import {
    buildBlockUniqueKey,
    canBePlacedIntoDirectBlockChild,
    createBlockFinder,
    deepClearSyncBlock,
    findBlockById,
    findNewSelectParentAndChildren,
    findParentBlockByType,
    getBlockAncestors,
    getBlockChildren,
    getCurrentBlockChildren,
    hasChildrenBlock,
    isButtonBlock,
    isContainerBlock,
    isCustomChildren,
    isCustomViewBlock,
    isFloatBoxBlock,
    isFormContainerBlock,
    isFormContainerChildren,
    isIconBlock,
    isTextBlock,
    PAGE_LAYER_ID,
    transformBlock2FlowLayoutNode,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import produce, { current, original } from 'immer'
import { useAtomCallback } from 'jotai/utils'
import { clone, find, findIndex, reduce } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import type { MakeADTMember } from 'ts-adt/MakeADT'

import { pageStackOfFloatBlockAtom, syncComponentsAtom, transformSyncComponentsAtom } from '@/atoms/application/state'
import { viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import {
    clearSelectionAtom,
    createBlockAtom,
    initContainerRuntimeStateAtom,
    removeBlockAtom,
    setPageInternalStateAtom
} from '@/atoms/page/action'
import {
    blocksAtom,
    containerRuntimeStateAtomFamily,
    lastPageOfStackAtom,
    pageAtomFamily,
    pageBlockContextIndexAtom,
    pageBlocksAtom,
    pageInternalStateAtomFamily
} from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'
import { AsideType } from '@/atoms/page/types'
import { useNodeToolbarActions } from '@/hooks/layoutEngine/useNodeToolbarActions'
import { copyBlock, includeNotAllowedBlock, removeBlockById } from '@/hooks/layoutEngine/utils'
import { useCurrentAppId, useCurrentBreakPointId, useCurrentEnvId, useMainBreakPoint } from '@/hooks/useApplication'
import { useBlockActions } from '@/hooks/useBlock'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import * as srv from '@/services'
import type { NodeUpdateAction, PageUndoRedoPayload } from '@/utils/undoRedo/page/controller'
import { NODE_UPDATE_ACTION, pageUndoRedoController } from '@/utils/undoRedo/page/controller'

import { PageSelector } from './PageSelector'
import { Item, SortableItem } from './SortableItem'
import type { FlattedNode } from './types'

const Root = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
`

const ListWrapper = styled.div`
    display: flex;
    flex: 1;
    padding: 4px 12px 32px;
    overflow: auto;
    /* background-color: var(--color-gray-100); */
`

const List = styled.ul`
    flex: 1;
    list-style: none;
    display: block;
    ${tinyButtons}
    border-radius: 5px;
`

function getNodeIdWithScope(value: string): NodeIdWithScope {
    if (value.includes('@')) {
        const [scope, id] = value.split('@')
        return { scope, id }
    }

    return { id: value }
}

function getCustomViewData(block: ViewBlockAbstract, dataSourceList: DataSourceAbstract[]) {
    const dataSource = find(ds => ds.id === block.config.pointer, dataSourceList)
    if (!dataSource) {
        return
    }
    return {
        name: block.title,
        dataSource
    }
}

type FlattenParams = {
    scope?: string
    getContainerState?: (containerId: string) => { currentView: string } | undefined
    indent: number
    parentId?: string
    collapsedNodes: NodeIdWithScope[]
    dataSourceList: DataSourceAbstract[]
    mainBreakPoint: string
    breakPointId: string
    customViewData?: CustomViewVisibleData
}
function flattenBlocks(blocks: BlockAbstract[], syncComponents: ContainerBlockAbstract[], option: FlattenParams): FlattedNode[] {
    const { scope, getContainerState, indent, parentId, collapsedNodes, dataSourceList, mainBreakPoint, breakPointId, customViewData } =
        option
    return blocks.reduce<FlattedNode[]>((total, current) => {
        const collapsed = collapsedNodes.some(item => item.id === current.id && item.scope === scope)

        const isContainer = isContainerBlock(current)
        const isFormContainer = isFormContainerBlock(current)
        const isFloatBox = isFloatBoxBlock(current)
        const isCustomView = isCustomViewBlock(current)
        const isText = isTextBlock(current)
        const isIcon = isIconBlock(current)
        const isButton = isButtonBlock(current)
        const collapsible = isContainer || isFormContainer || isCustomView || isFloatBox || isText || isIcon || isButton

        const currentCustomViewData: CustomViewVisibleData | undefined = isCustomView
            ? getCustomViewData(current, dataSourceList)
            : undefined

        let title: string | undefined
        let children: BlockAbstract[] | undefined
        if (current.synchronousId) {
            const block = syncComponents.find(item => item.id === current.synchronousId)
            if (block) {
                title = block.title
                children = getCurrentBlockChildren(block, { getContainerState, uniqueContainerId: current.id })
            }
        } else {
            title = current.title
            children = getCurrentBlockChildren(current, {
                getContainerState,
                uniqueContainerId: scope ? `${scope}@${current.id}` : current.id
            })
        }

        const visibleMode = current.config.breakPoint?.visibility?.visible
        return [
            ...total,
            {
                id: current.id,
                scope,
                name: title || getBlockName(current),
                icon: current.synchronousId ? 'SyncComponent' : getBlockIcon(current, breakPointId, mainBreakPoint),
                indent,
                parentId,
                collapsible,
                collapsed,
                block: current,
                customViewData,
                description: isFormContainer ? dataSourceList.find(item => item.id === current.config.pointer)?.name : undefined,
                visibleMode
            },
            ...(collapsed
                ? []
                : flattenBlocks(children || [], syncComponents, {
                      scope: scope || (current.synchronousId ? current.id : undefined),
                      getContainerState,
                      indent: indent + 1,
                      parentId: current.id,
                      collapsedNodes,
                      dataSourceList,
                      breakPointId,
                      mainBreakPoint,
                      customViewData: currentCustomViewData || customViewData
                  }))
        ]
    }, [])
}

function buildPageBlocksTree(
    nodes: FlattedNode[],
    getContainerState?: (containerId: string) => { currentView: string } | undefined
): BlockAbstract[] {
    const tree: BlockAbstract[] = []
    const map: Record<string, BlockAbstract> = {}

    nodes.forEach(node => {
        if (!node.block) {
            return
        }

        const block = clone(node.block)

        if (isContainerBlock(block)) {
            const views = block.children
            views.find(view => view.id === getContainerState?.(block.id)?.currentView)?.children.splice(0)
            map[block.id] = {
                ...block,
                children: views
            }

            return
        }

        if (hasChildrenBlock(block)) {
            map[block.id] = {
                ...block,
                children: []
            }
            return
        }

        map[block.id] = block
    })

    nodes.forEach(node => {
        if (!node.parentId) {
            tree.push(map[node.id])
            return
        }

        if (node.parentId && map[node.parentId]) {
            const data = map[node.id]

            const parent = map[node.parentId]
            if (isContainerBlock(parent)) {
                const view = parent.children.find(item => item.id === getContainerState?.(parent.id)?.currentView)
                if (!view) {
                    return
                }
                view.children.push(data)
                return
            }

            if (hasChildrenBlock(parent)) {
                parent.children?.push(data)
            }
        }
    })

    return tree
}

function getParentId(indent: number, overIndex: number, items: FlattedNode[], prevItem?: FlattedNode): NodeIdWithScope | undefined {
    if (indent === 1 || !prevItem) {
        return
    }
    if (indent === prevItem.indent) {
        return prevItem.parentId
            ? { id: prevItem.parentId, scope: prevItem.scope === prevItem.parentId ? undefined : prevItem.scope }
            : undefined
    }
    if (indent > prevItem.indent) {
        return { id: prevItem.id, scope: prevItem.scope }
    }
    const less = items
        .slice(0, overIndex)
        .reverse()
        .find(item => item.indent === indent)

    return less?.parentId ? { id: less.parentId, scope: less.scope === less.parentId ? undefined : less.scope } : undefined
}

/** 检查是否包含禁止的block */
function checkNotAllowed({
    fromBlock,
    parentBlock,
    blocks
}: {
    fromBlock: BlockAbstract
    parentBlock: BlockAbstract
    blocks: BlockAbstract[]
}) {
    // 表单容器、视图及图表block禁止插入到自定义视图内
    if (
        includeNotAllowedBlock(fromBlock, {
            notAllowedList: [BlockType.view, BlockType.chart],
            viewTypeFilter: block => block.type === BlockType.view && block.config?.viewType === 'custom'
        }) &&
        (isCustomViewBlock(parentBlock) || isCustomChildren(parentBlock.id, blocks))
    ) {
        Toast.error('自定义视图不允许拖入自定义视图')
        return false
    }
    // 表单容器、视图及图表block禁止插入到弹出窗口内
    // if (includeNotAllowedBlock(fromBlock, [BlockType.formContainer, BlockType.view, BlockType.chart]) && isFloatBoxBlock(parentBlock)) {
    //     Toast.error('视图、图表、表单容器，不允许拖入弹出窗口')
    //     return false
    // }

    // 子表单只能拖入到表单页面的表单容器中
    if (
        includeNotAllowedBlock(fromBlock, { notAllowedList: [BlockType.subForm] }) &&
        (!isFormContainerBlock(parentBlock) || !isFormContainerChildren(parentBlock.id, blocks))
    ) {
        Toast.error('子表单只能拖入到表单容器中')
        return false
    }

    return true
}

const measuring = {
    droppable: {
        strategy: MeasuringStrategy.Always
    }
}
const adjustTranslate: Modifier = ({ transform }) => {
    return {
        ...transform,
        y: transform.y - 25
    }
}

export const PageLayers: React.FC = () => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const selectedNodeRef = useRef<NodeIdWithScope | undefined>()
    const scrollRef = useRef<HTMLDivElement>(null)
    const [pageId, stackId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.pageId || '', s?.stackId || ''] as const, [])
    )
    const { asideType = AsideType.PAGE, selectedNodes = [] } = useAtomData(pageInternalStateAtomFamily(stackId))
    // 创建获取容器状态的函数
    const getContainerState = useAtomCallback(
        useCallback(
            (get, set, containerId: string) => get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined,
            [stackId]
        )
    )
    const selectedNode = useMemo(() => (selectedNodes.length === 1 ? selectedNodes[0] : undefined), [selectedNodes])

    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name || '', [])
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const mainBreakPoint = useMainBreakPoint()
    const breakPointId = useCurrentBreakPointId()

    const pageBlocks = useAtomData(pageBlocksAtom(pageId))
    const syncComponents = useAtomData(transformSyncComponentsAtom)
    const pageBlockContextIndex = useAtomData(pageBlockContextIndexAtom)

    const { run: setPageStackOfFloatBlock } = useAtomAction(pageStackOfFloatBlockAtom)
    const { run: clearSelection } = useAtomAction(clearSelectionAtom)
    const { run: setPageInternalState } = useAtomAction(setPageInternalStateAtom)

    const [collapsedNodes, setCollapsedNodes] = useState<NodeIdWithScope[]>([])
    const [activeNode, setActiveNode] = useState<NodeIdWithScope>()

    useEffect(() => {
        if (selectedNode && selectedNodeRef.current !== selectedNode) {
            selectedNodeRef.current = selectedNode
            const layerDom = document.querySelector(
                `li[data-layer-id="${selectedNode.id}"]${selectedNode.scope ? `[data-layer-scope="${selectedNode.scope}"]` : ''}`
            )
            if (!layerDom) {
                return
            }
            const label = layerDom.querySelector('label')
            label?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
        }
    }, [selectedNode])

    const handleGetViewFirstRecordId = useAtomCallback(
        useCallback(
            (get, set, viewId: string, dsId: string) => {
                return get(viewIndependentDataAtomFamily({ stackId, viewId, dsId })).recordPoints[0]?.id
            },
            [stackId]
        )
    )

    const flattedPageNodes = useMemo(() => {
        const collapsed = collapsedNodes.some(item => item.id === pageId && !item.scope)
        return collapsed
            ? []
            : flattenBlocks(pageBlocks, syncComponents, {
                  getContainerState,
                  indent: 1,
                  collapsedNodes: activeNode ? [...collapsedNodes, activeNode] : collapsedNodes,
                  mainBreakPoint,
                  breakPointId,
                  dataSourceList
              })
    }, [activeNode, getContainerState, collapsedNodes, dataSourceList, mainBreakPoint, pageBlocks, pageId, breakPointId, syncComponents])

    const items = useMemo(() => flattedPageNodes.map(item => (item.scope ? `${item.scope}@${item.id}` : item.id)), [flattedPageNodes])

    const activeFlattedNode = useMemo(
        () => activeNode && flattedPageNodes.find(item => item.id === activeNode.id && item.scope === activeNode.scope),
        [activeNode, flattedPageNodes]
    )

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 4 }
        })
    )
    const [overInfo, setOverInfo] = useState<{ indent: number; parentId?: NodeIdWithScope } | null>(null)

    const onDragStart = (e: DragStartEvent) => {
        if (e.active) {
            setActiveNode(getNodeIdWithScope(e.active.id.toString()))
        }
    }

    const onDragMove = ({ delta, active, over }: DragMoveEvent) => {
        if (over) {
            const activeIndex = items.indexOf(active.id as string)
            const overIndex = items.indexOf(over.id as string)
            const activeItem = flattedPageNodes[activeIndex]
            const newItems = arrayMove(flattedPageNodes, activeIndex, overIndex)
            const prevItem = newItems[overIndex - 1]
            const nextItem = newItems[overIndex + 1]
            const currentIndent = activeItem.indent + Math.round(delta.x / 20)

            if ((prevItem && !prevItem.block) || !activeItem.block) {
                return
            }

            const max = prevItem
                ? prevItem.collapsible && prevItem.block && canBePlacedIntoDirectBlockChild(prevItem.block, activeItem.block)
                    ? prevItem.indent + 1
                    : prevItem.indent
                : 1
            const min = nextItem ? nextItem.indent : 1
            const indent = Math.max(min, Math.min(max, currentIndent))
            const parentId = getParentId(indent, overIndex, newItems, prevItem)

            if (activeIndex === overIndex && indent === activeItem.indent) {
                setOverInfo(null)

                return
            }

            setOverInfo({ indent, parentId })
        } else {
            setOverInfo(null)
        }
    }

    // 1. 从同步组件内移入到普通组件 -- 删除同步组件、创建普通组件
    // 2. 从同步组件内移入到同步组件 -- 更新同步组件
    // 2-1. 不同作用域下的操作非法
    // 3. 从普通组件移入到普通组件   -- 更新普通组件
    // 4. 从普通组件移入到同步组件   -- 删除普通组件、创建同步组件
    const onDragEnd = useAtomCallback(
        useCallback(
            async (get, set, { over, active }: DragEndEvent) => {
                reset()

                if (over && overInfo) {
                    const activeNode = getNodeIdWithScope(active.id.toString())
                    const overNode = getNodeIdWithScope(over.id.toString())
                    const { indent, parentId } = overInfo
                    const flattenNodes = flattenBlocks(pageBlocks, syncComponents, {
                        getContainerState,
                        indent: 1,
                        collapsedNodes: [],
                        mainBreakPoint,
                        breakPointId,
                        dataSourceList
                    })
                    const activeIndex = flattenNodes.findIndex(item => item.id === activeNode.id && item.scope === activeNode.scope)
                    const overIndex = flattenNodes.findIndex(item => item.id === overNode.id && item.scope === overNode.scope)

                    const fromNode = flattenNodes[activeIndex]
                    const toNode = flattenNodes[overIndex]
                    const fromBlock = fromNode.block
                    if (!fromBlock) {
                        return
                    }

                    const parentNode = parentId
                        ? flattenNodes.find(item => item.id === parentId.id && item.scope === parentId.scope)
                        : undefined
                    const scope = parentNode?.block?.synchronousId ? parentNode.id : parentNode?.scope
                    const isMoveToSync = !!parentNode && (!!parentNode.block?.isLeafSynchronous || !!parentNode.block?.synchronousId)

                    if (
                        parentNode?.block &&
                        !checkNotAllowed({
                            fromBlock,
                            parentBlock: parentNode.block,
                            blocks: pageBlocks
                        })
                    ) {
                        return
                    }

                    if (
                        fromBlock.synchronousId &&
                        parentNode?.block &&
                        (parentNode.block.synchronousId || parentNode.block.isLeafSynchronous)
                    ) {
                        Toast.error('同步组件不能拖入至同步组件')
                        return
                    }

                    if (fromBlock.isLeafSynchronous && parentNode?.block) {
                        if (parentNode.block.synchronousId && parentNode.block.id !== fromNode.scope) {
                            Toast.error('同步组件不能拖入至同步组件')
                            return
                        }

                        if (parentNode.block.isLeafSynchronous && parentNode.scope !== fromNode.scope) {
                            Toast.error('同步组件不能拖入至同步组件')
                            return
                        }
                    }

                    const sortedNodes = arrayMove(
                        produce(flattenNodes, draft => {
                            const draftNode = draft[activeIndex]
                            if (!draftNode.block) {
                                return
                            }
                            draftNode.parentId = parentId?.id
                            draftNode.indent = indent
                            if (isMoveToSync) {
                                draftNode.scope = parentNode?.scope ?? parentNode?.block?.synchronousId
                                draftNode.block.isLeafSynchronous = true
                            } else {
                                draftNode.scope = undefined
                                draftNode.block.isLeafSynchronous = undefined
                            }
                        }),
                        activeIndex,
                        overIndex
                    )

                    // 过滤掉同步组件下的元素
                    const filterSyncNodes = sortedNodes.filter(item => !item.block?.isLeafSynchronous)

                    const newBlocks = buildPageBlocksTree(filterSyncNodes, getContainerState)
                    const blockUndoRedoInfo: MakeADTMember<'action', PageUndoRedoPayload, NodeUpdateAction>['payload'] = {}

                    const isSyncBlock2Block =
                        fromBlock.isLeafSynchronous &&
                        (!parentNode || (!parentNode.block?.isLeafSynchronous && !parentNode.block?.synchronousId))
                    const isSyncBlock2SyncBlock =
                        fromBlock.isLeafSynchronous &&
                        !!parentNode &&
                        (!!parentNode.block?.isLeafSynchronous || !!parentNode.block?.synchronousId)
                    const isBlock2Block =
                        !fromBlock.isLeafSynchronous &&
                        (!parentNode || (!parentNode.block?.isLeafSynchronous && !parentNode.block?.synchronousId))
                    const isBlock2SyncBlock =
                        !fromBlock.isLeafSynchronous &&
                        !!parentNode &&
                        (!!parentNode.block?.isLeafSynchronous || !!parentNode.block?.synchronousId)

                    if (isSyncBlock2Block) {
                        set(syncComponentsAtom, draft => {
                            const removed = removeBlockById(fromBlock.id)(draft)
                            if (!removed) {
                                return
                            }

                            if (!blockUndoRedoInfo.removed) {
                                blockUndoRedoInfo.removed = {}
                            }
                            blockUndoRedoInfo.removed.removedSyncComponents = removed.map(current)
                            blockUndoRedoInfo.removed.prevSyncComponents = original(draft)
                            blockUndoRedoInfo.removed.nextSyncComponents = current(draft)

                            if (!blockUndoRedoInfo.created) {
                                blockUndoRedoInfo.created = {}
                            }
                            blockUndoRedoInfo.created.createdBlocks = copyBlock(removed.map(current))
                            blockUndoRedoInfo.created.createdBlocks.forEach(deepClearSyncBlock)
                        })
                        set(blocksAtom, draft => {
                            const draftBlocks = draft[pageId]
                            if (!draftBlocks || !blockUndoRedoInfo.created) {
                                return
                            }
                            blockUndoRedoInfo.created.prevBlocks = original(draftBlocks)
                            if (blockUndoRedoInfo.created.createdBlocks) {
                                const oldBlock = findBlockById(fromBlock.id, newBlocks)
                                if (oldBlock) {
                                    Object.entries(blockUndoRedoInfo.created.createdBlocks[0]).forEach(([k, v]) => {
                                        Reflect.set(oldBlock, k, v)
                                    })
                                }
                            }
                            draft[pageId] = newBlocks
                            blockUndoRedoInfo.created.nextBlocks = newBlocks
                        })
                    }

                    if (isSyncBlock2SyncBlock) {
                        set(syncComponentsAtom, draft => {
                            const removed = removeBlockById(fromBlock.id)(draft)
                            if (!removed) {
                                return
                            }

                            const { id, block, scope } = parentNode
                            if (block?.synchronousId) {
                                // 拖入到同步组件中
                                const draftParent = findBlockById(block.synchronousId, draft)
                                if (!draftParent) {
                                    return
                                }

                                const children = getCurrentBlockChildren(draftParent, {
                                    getContainerState,
                                    uniqueContainerId: id
                                })
                                if (!children) {
                                    return
                                }

                                const index = toNode.id === id ? 0 : (original(children) || []).findIndex(item => item.id === toNode.id)
                                const newBlock = clone({ ...fromBlock, isLeafSynchronous: true })
                                if (index === -1) {
                                    children.push(newBlock)
                                } else {
                                    children.splice(index, 0, newBlock)
                                }

                                blockUndoRedoInfo.prev = {
                                    blocks: undefined,
                                    syncComponents: original(draft)
                                }
                                blockUndoRedoInfo.next = {
                                    blocks: undefined,
                                    syncComponents: current(draft)
                                }
                            } else if (block?.isLeafSynchronous) {
                                // 拖入到子同步组件中
                                const draftParent = findBlockById(id, draft)
                                if (!draftParent) {
                                    return
                                }

                                const children = getCurrentBlockChildren(draftParent, {
                                    getContainerState,
                                    uniqueContainerId: `${scope}@${id}`
                                })
                                if (!children) {
                                    return
                                }

                                const index = toNode.id === id ? 0 : (original(children) || []).findIndex(item => item.id === toNode.id)

                                const newBlock = clone({ ...fromBlock, isLeafSynchronous: true })
                                if (index === -1) {
                                    children.push(newBlock)
                                } else {
                                    children.splice(index, 0, newBlock)
                                }

                                blockUndoRedoInfo.prev = {
                                    blocks: undefined,
                                    syncComponents: original(draft)
                                }
                                blockUndoRedoInfo.next = {
                                    blocks: undefined,
                                    syncComponents: current(draft)
                                }
                            }
                        })
                    }

                    if (isBlock2Block) {
                        set(blocksAtom, draft => {
                            const draftBlocks = draft[pageId]
                            if (!draftBlocks) {
                                return
                            }

                            blockUndoRedoInfo.prev = {
                                blocks: original(draftBlocks),
                                syncComponents: undefined
                            }

                            if (blockUndoRedoInfo.created?.createdBlocks) {
                                const oldBlock = findBlockById(fromBlock.id, newBlocks)
                                if (oldBlock) {
                                    Object.entries(blockUndoRedoInfo.created.createdBlocks[0]).forEach(([k, v]) => {
                                        Reflect.set(oldBlock, k, v)
                                    })
                                }
                            }
                            draft[pageId] = newBlocks

                            blockUndoRedoInfo.next = {
                                blocks: newBlocks,
                                syncComponents: undefined
                            }
                        })
                    }

                    if (isBlock2SyncBlock) {
                        set(syncComponentsAtom, draft => {
                            const removed = removeBlockById(fromBlock.id)(draft)
                            if (removed) {
                                blockUndoRedoInfo.removed = {
                                    removedSyncComponents: removed.map(current),
                                    prevSyncComponents: original(draft),
                                    nextSyncComponents: current(draft)
                                }
                            }
                        })

                        set(blocksAtom, draft => {
                            const draftBlocks = draft[pageId]
                            if (!draftBlocks) {
                                return
                            }

                            if (!blockUndoRedoInfo.removed?.removedSyncComponents) {
                                return
                            }

                            const createdBlocks = copyBlock(blockUndoRedoInfo.removed.removedSyncComponents)
                            createdBlocks.forEach(deepClearSyncBlock)
                            blockUndoRedoInfo.created = {
                                createdBlocks,
                                prevBlocks: original(draftBlocks),
                                nextBlocks: newBlocks
                            }
                            draft[pageId] = newBlocks
                        })
                    }

                    if (blockUndoRedoInfo.removed) {
                        if (blockUndoRedoInfo.removed.removedBlocks && blockUndoRedoInfo.removed.nextBlocks) {
                            await set(removeBlockAtom, {
                                pageId,
                                stackId,
                                removedBlocks: blockUndoRedoInfo.removed.removedBlocks,
                                blocks: blockUndoRedoInfo.removed.nextBlocks,
                                clearSelected: false
                            })
                        }

                        if (blockUndoRedoInfo.removed.removedSyncComponents && blockUndoRedoInfo.removed.nextSyncComponents) {
                            set(initContainerRuntimeStateAtom, {
                                stackId,
                                blocks: get(pageBlocksAtom(pageId)),
                                syncComponents: blockUndoRedoInfo.removed.nextSyncComponents
                            })
                            set(pageInternalStateAtomFamily(stackId), draft => {
                                draft.selectedNodes = [{ id: fromBlock.id, scope }]
                            })
                            await srv.deleteSyncLeafBlocks({
                                pageId,
                                removedBlocks: blockUndoRedoInfo.removed.removedSyncComponents,
                                blocks: blockUndoRedoInfo.removed.nextSyncComponents
                            })
                        }
                    }

                    if (blockUndoRedoInfo.created) {
                        if (blockUndoRedoInfo.created.createdBlocks && blockUndoRedoInfo.created.nextBlocks) {
                            await set(createBlockAtom, {
                                pageId,
                                stackId,
                                createdBlocks: blockUndoRedoInfo.created.createdBlocks,
                                blocks: blockUndoRedoInfo.created.nextBlocks,
                                scope
                            })
                        }

                        if (blockUndoRedoInfo.created.createdSyncComponents && blockUndoRedoInfo.created.nextSyncComponents) {
                            set(initContainerRuntimeStateAtom, {
                                stackId,
                                blocks: get(pageBlocksAtom(pageId)),
                                syncComponents: blockUndoRedoInfo.created.nextSyncComponents
                            })
                            set(pageInternalStateAtomFamily(stackId), draft => {
                                draft.selectedNodes = [{ id: fromBlock.id, scope }]
                            })
                            await srv.createSyncBlock({
                                createBlocks: blockUndoRedoInfo.created.createdSyncComponents,
                                blocks: blockUndoRedoInfo.created.nextSyncComponents
                            })
                        }
                    }

                    if (blockUndoRedoInfo.next) {
                        if (blockUndoRedoInfo.next.blocks) {
                            await srv.updateBlock({ pageId, allBlocks: blockUndoRedoInfo.next.blocks })
                        }
                        if (blockUndoRedoInfo.next.syncComponents) {
                            await srv.updateSyncComponents(blockUndoRedoInfo.next.syncComponents)
                        }
                    }

                    pageUndoRedoController.add({
                        action: NODE_UPDATE_ACTION,
                        payload: blockUndoRedoInfo
                    })
                }
            },
            [dataSourceList, getContainerState, mainBreakPoint, overInfo, pageBlocks, pageId, breakPointId, stackId, syncComponents]
        )
    )

    const onDragCancel = () => {
        reset()
    }

    const reset = () => {
        setActiveNode(undefined)
        setOverInfo(null)
    }

    const actions = useNodeToolbarActions({ pageId, stackId })

    const { onUpdateBlock } = useBlockActions(pageId, stackId)

    // 从已展开的图层列表推导「选中节点的后代」集合，与列表完全一致，避免依赖 findWithChildren（其返回同步组件定义树，无实例 id）
    const selectedChildrenIds = useMemo(() => {
        if (selectedNodes.length === 0 || flattedPageNodes.length === 0) {
            return new Set<string>()
        }

        const selectedKeys = new Set(selectedNodes.map(n => buildBlockUniqueKey(n.id, n.scope)))
        const descendantKeys = new Set<string>()

        // 按展平顺序，每个节点的父节点是「上一时刻」indent-1 的节点
        const keyToParentKey = new Map<string, string | undefined>()
        const lastKeyByIndent: Record<number, string> = {}

        for (const node of flattedPageNodes) {
            const key = buildBlockUniqueKey(node.id, node.scope)
            const parentKey = node.indent > 1 ? lastKeyByIndent[node.indent - 1] : undefined
            keyToParentKey.set(key, parentKey)
            lastKeyByIndent[node.indent] = key
        }

        for (const node of flattedPageNodes) {
            const key = buildBlockUniqueKey(node.id, node.scope)
            if (selectedKeys.has(key)) continue
            let current: string | undefined = key
            while (current) {
                if (selectedKeys.has(current)) {
                    descendantKeys.add(key)
                    break
                }
                current = keyToParentKey.get(current)
            }
        }

        return descendantKeys
    }, [selectedNodes, flattedPageNodes])

    return (
        <Root>
            <PageSelector pageId={pageId} />

            <Divider my={8} />

            <ListWrapper tabIndex={-1} id={PAGE_LAYER_ID} ref={scrollRef}>
                <List data-ignore-click-away>
                    <Item
                        data={{
                            id: 'page',
                            indent: 0,
                            name: pageName,
                            icon: 'PageBroadContent',
                            collapsed: collapsedNodes.some(item => item.id === pageId && !item.scope),
                            collapsible: true
                        }}
                        onCollapseChange={v => {
                            setCollapsedNodes(s => {
                                if (v) {
                                    return [...s, { id: pageId }]
                                }
                                return s.filter(node => node.id !== pageId)
                            })
                        }}
                        isSelected={asideType === AsideType.PAGE && selectedNodes.length === 0}
                        onClick={() => {
                            clearSelection(stackId)
                        }}
                    />
                    <DndContext
                        sensors={sensors}
                        measuring={measuring}
                        collisionDetection={closestCenter}
                        onDragStart={onDragStart}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        onDragCancel={onDragCancel}
                    >
                        <SortableContext disabled={disabledWithVersion} items={items} strategy={verticalListSortingStrategy}>
                            {flattedPageNodes.map(item => (
                                <SortableItem
                                    key={item.scope ? `${item.scope}@${item.id}` : item.id}
                                    disabled={item.block?.type === BlockType.floatBox}
                                    isSelected={selectedNodes.some(node => node.id === item.id && node.scope === item.scope)}
                                    isSelectedChildren={selectedChildrenIds.has(buildBlockUniqueKey(item.id, item.scope))}
                                    data={{
                                        ...item,
                                        indent:
                                            activeNode && activeNode.id === item.id && activeNode.scope === item.scope && overInfo
                                                ? overInfo.indent
                                                : item.indent
                                    }}
                                    onNameChange={v =>
                                        item.block &&
                                        onUpdateBlock({
                                            nextBlock: produce(item.block, draft => void (draft.title = v)),
                                            prevBlock: item.block
                                        })
                                    }
                                    onCollapseChange={v => {
                                        setCollapsedNodes(s => {
                                            if (v) {
                                                return [...s, { id: item.id, scope: item.scope }]
                                            }
                                            return s.filter(node => !(node.id === item.id && node.scope === item.scope))
                                        })
                                    }}
                                    onDeleteItem={async () => {
                                        if (item.collapsible) {
                                            const isConfirm = await Modal.confirm({
                                                title: '确认删除',
                                                content: '删除后，该节点之后的其他节点也将一并删除，且不可恢复，请谨慎操作！'
                                            })
                                            if (!isConfirm) {
                                                return
                                            }
                                        }
                                        actions.onRemove([{ id: item.id, scope: item.scope }])
                                    }}
                                    onClick={e => {
                                        const isShift = e.shiftKey

                                        // 多选
                                        if (isShift) {
                                            window.getSelection()?.removeAllRanges()

                                            // 多选逻辑
                                            const currentSelectedNodes = selectedNodes ?? []
                                            let newSelectedNodes: NodeIdWithScope[] = []

                                            if (currentSelectedNodes.some(node => node.id === item.id && node.scope === item.scope)) {
                                                newSelectedNodes = currentSelectedNodes.filter(
                                                    (n: NodeIdWithScope) => n.id !== item.id || n.scope !== item.scope
                                                )
                                            } else {
                                                const flowNodes = transformBlock2FlowLayoutNode({
                                                    blocks: pageBlocks,
                                                    syncComponents
                                                })

                                                const { parent, children } = findNewSelectParentAndChildren(
                                                    { id: item.id, scope: item.scope },
                                                    currentSelectedNodes,
                                                    flowNodes
                                                )

                                                newSelectedNodes = [...currentSelectedNodes]
                                                // 如果有父级选中了，则取消选中父级
                                                if (parent) {
                                                    newSelectedNodes = currentSelectedNodes.filter(
                                                        (item: NodeIdWithScope) => item.scope !== parent.scope || item.id !== parent.id
                                                    )
                                                }

                                                // 如果有子级选中了，则取消选中子级
                                                if (children.length > 0) {
                                                    newSelectedNodes = currentSelectedNodes.filter(
                                                        (item: NodeIdWithScope) =>
                                                            !children.some(
                                                                (child: NodeIdWithScope) =>
                                                                    child.scope === item.scope && child.id === item.id
                                                            )
                                                    )
                                                }

                                                newSelectedNodes = [...newSelectedNodes, { id: item.id, scope: item.scope }]
                                            }

                                            setPageInternalState({
                                                stackId,
                                                state: {
                                                    asideType: newSelectedNodes.length === 1 ? AsideType.BLOCK : AsideType.PAGE,
                                                    selectedNodes: newSelectedNodes
                                                }
                                            })

                                            return
                                        }

                                        if (!pageBlockContextIndex) {
                                            return
                                        }
                                        // getBlockAncestors 返回祖先 context 链 [根→父]，筛出浮层后若当前节点是浮层则追加自身
                                        const itemUniqueKey = buildBlockUniqueKey(item.id, item.scope)
                                        const ancestorContexts = getBlockAncestors(itemUniqueKey, pageBlockContextIndex)
                                        const floatContexts = ancestorContexts.filter(ctx => ctx.block.type === BlockType.floatBox)
                                        if (item.block?.type === BlockType.floatBox) {
                                            const selfCtx = pageBlockContextIndex.contextMap.get(itemUniqueKey)
                                            if (selfCtx) floatContexts.push(selfCtx)
                                        }
                                        setPageStackOfFloatBlock(
                                            floatContexts.map(ctx => ({
                                                blockId: ctx.block.id,
                                                stackId,
                                                scope: ctx.scope,
                                                open: true,
                                                recordId:
                                                    ctx.customView?.id && ctx.customView?.config.pointer
                                                        ? handleGetViewFirstRecordId(ctx.customView.id, ctx.customView.config.pointer)
                                                        : undefined
                                            }))
                                        )
                                        console.log('🚀 ~ PageLayers ~ floatContexts:', floatContexts)

                                        // 选中block
                                        selectedNodeRef.current = { id: item.id, scope: item.scope }
                                        setPageInternalState({
                                            stackId,
                                            state: {
                                                asideType: AsideType.BLOCK,
                                                selectedNodes: [{ id: item.id, scope: item.scope }]
                                            }
                                        })
                                    }}
                                />
                            ))}

                            {createPortal(
                                <DragOverlay modifiers={[adjustTranslate]} style={{ opacity: 0.8 }}>
                                    {activeFlattedNode && <Item key="overlay" data={activeFlattedNode} isOverlay />}
                                </DragOverlay>,
                                document.body
                            )}
                        </SortableContext>
                    </DndContext>
                </List>
            </ListWrapper>
        </Root>
    )
}
